/** What the attention strip lists: the things that go wrong without anyone
 *  looking (plan 1-3) — the sync failing, AdMob failing (revenue is the
 *  source), the sync falling behind, alerts not yet read. Built from the
 *  sync runs, the Google status and the alerts — every one an endpoint that
 *  already exists.
 *
 *  Google Ads refusing access (403) is not here: it is a known state that can
 *  last weeks, and a band on every page for it would be alarm fatigue. 오늘's
 *  setup notes and the campaign page say it instead. */

import type { AlertRow, GoogleStatus } from '@/api/client'
import type { JobRun } from '@/admin-design/_common/jobs/types'
import { adsError, newestSyncRuns, spendState, syncDetails, syncKey } from './spend'
import { jobLabel, statusOf } from './status'

export type AttentionTone = 'failed' | 'publishing' | 'neutral'

export interface AttentionDetail {
  /** Where it happened: the job, the service. */
  where: string
  /** What it was: a count, a time. */
  what?: string
  /** The job's or the service's own words, whole. */
  message?: string
  at?: string
}

export interface AttentionItem {
  key: string
  tone: AttentionTone
  label: string
  count: number
  to: string
  details: AttentionDetail[]
}

export interface AttentionInput {
  /** Recent sync runs (any order). */
  runs?: JobRun[] | null
  status?: GoogleStatus | null
  alerts?: AlertRow[] | null
  now?: Date
}

const SYNC_JOBS = `/jobs?job=sync`

const SEVERITY_TONE: Record<string, AttentionTone> = { critical: 'failed', warning: 'publishing' }

export function attentionItems(input: AttentionInput): AttentionItem[] {
  const items: AttentionItem[] = []
  const { finished } = newestSyncRuns(input.runs)
  const key = syncKey({ runs: input.runs, status: input.status, now: input.now })

  if (finished?.status === 'error') {
    items.push({
      key: 'sync-failed',
      tone: 'failed',
      label: '동기화 실패',
      count: 0,
      to: SYNC_JOBS,
      details: [{ where: jobLabel('sync'), message: finished.message || '사유가 기록되지 않았다', at: finished.finished_at || finished.started_at }],
    })
  }

  const admobErr = finished?.status === 'ok' ? syncDetails(finished)?.admob_error : undefined
  if (admobErr) {
    items.push({
      key: 'admob-failed',
      tone: 'failed',
      label: 'AdMob 오류',
      count: 0,
      to: SYNC_JOBS,
      details: [{ where: 'AdMob', message: admobErr, at: finished!.finished_at || finished!.started_at }],
    })
  }

  if (key === 'stale') {
    const last = input.status?.last_sync_at
    const hours = last ? Math.floor(((input.now ?? new Date()).getTime() - Date.parse(last)) / 3_600_000) : 0
    items.push({
      key: 'sync-stale',
      tone: 'publishing',
      label: '동기화 오래됨',
      count: 0,
      to: SYNC_JOBS,
      details: [{ where: jobLabel('sync'), what: hours > 0 ? `${hours}시간째 성공한 실행 없음 · 주기 1시간` : '성공한 실행 없음', at: last || undefined }],
    })
  }

  const unread = (input.alerts ?? []).filter((a) => !a.is_read)
  if (unread.length > 0) {
    const tone: AttentionTone = unread.some((a) => a.severity === 'critical')
      ? 'failed'
      : unread.some((a) => SEVERITY_TONE[a.severity] === 'publishing')
        ? 'publishing'
        : 'neutral'
    items.push({
      key: 'alerts-unread',
      tone,
      label: '안 읽은 알림',
      count: unread.length,
      to: '/alerts?state=unread',
      details: unread.slice(0, 5).map((a) => ({ where: statusOf('alertSeverity', a.severity).label, what: a.title })),
    })
  }

  return items
}

const GOOGLE_PAGE = '/settings/google'

/** The lines only 오늘's "확인할 것" list shows: nothing is broken, but
 *  something is not set up. Not alarms, so the strip leaves them out. */
export function setupNotes(input: { status?: GoogleStatus | null; runs?: JobRun[] | null }): AttentionItem[] {
  const s = input.status
  if (!s) return []
  const notes: AttentionItem[] = []
  if (!s.connected) {
    notes.push({
      key: 'google-offline',
      tone: 'neutral',
      label: 'Google 계정을 연결한다',
      count: 0,
      to: GOOGLE_PAGE,
      details: [{ where: 'Google 연결', what: '연결하면 AdMob 수익과 Google Ads 지출이 매시간 들어온다' }],
    })
    return notes
  }
  if (!s.admob_account_id) {
    notes.push({
      key: 'admob-unset',
      tone: 'neutral',
      label: 'AdMob 게시자 ID가 없다',
      count: 0,
      to: GOOGLE_PAGE,
      details: [{ where: 'AdMob', what: '게시자 ID(pub-…)가 있어야 수익을 가져온다' }],
    })
  }
  const spend = spendState(input)
  if (spend === 'blocked' || spend === 'error') {
    const err = adsError(input.runs)
    notes.push({
      key: `ads-${spend}`,
      tone: 'neutral',
      label: spend === 'blocked' ? 'Google Ads API 액세스 미승인 — 지출이 들어오지 않는다' : 'Google Ads 조회 오류 — 지출이 들어오지 않는다',
      count: 0,
      to: GOOGLE_PAGE,
      details: [{ where: 'Google Ads', message: err?.message, at: err?.at }],
    })
  } else if (spend === 'unlinked') {
    notes.push({
      key: 'ads-unlinked',
      tone: 'neutral',
      label: 'Google Ads 미연결',
      count: 0,
      to: GOOGLE_PAGE,
      details: [{ where: 'Google Ads', what: '연결하면 캠페인 지출과 앱별 ROAS가 보인다' }],
    })
  }
  return notes
}
